import React from 'react';
import {
    formatGoodLeapSyncResyncDisplayLines,
    loanSyncHasStoredErrors,
    mapGoodLeapResyncSystemDesignDisplay,
    parseLoanSyncRemarkDescriptions,
    type LoanSyncResyncErrors,
} from './contractFlowHelpers';
import './oneButton-css/LoanSyncErrorsDialogContent.css';

export interface LoanSyncErrorsDialogContentProps {
    loanStatus: Record<string, unknown>;
    financePartner: string;
    resyncErrors?: LoanSyncResyncErrors;
}

type ErrorBlock = { label: string; lines: string[] };

import { XCircle } from '@phosphor-icons/react';

function SyncErrorBlock({ label, lines }: ErrorBlock) {
    return (
        <div className="obd-loan-sync-errors__block">
            <strong className="obd-loan-sync-errors__label">{label}:</strong>
            <ul className="obd-loan-sync-errors__list">
                {lines.map((line, i) => (
                    <li key={`${label}-${i}`}>{line}</li>
                ))}
            </ul>
        </div>
    );
}

/** Trinity `DetailCellRendererComponent.showSyncErrorPopup` — stored remarks + resync errors. */
export const LoanSyncErrorsDialogContent: React.FC<LoanSyncErrorsDialogContentProps> = ({
    loanStatus,
    financePartner,
    resyncErrors,
}) => {
    const storedBlocks: ErrorBlock[] = [];

    const systemLines = parseLoanSyncRemarkDescriptions(
        'System Design',
        loanStatus.System_Design_Sync_Remarks,
        financePartner,
    );
    if (systemLines.length) storedBlocks.push({ label: 'System Design', lines: systemLines });

    const utilityLines = parseLoanSyncRemarkDescriptions('Utility', loanStatus.Utility_Sync_Remarks, financePartner);
    if (utilityLines.length) storedBlocks.push({ label: 'Utility', lines: utilityLines });

    const docStatus = loanStatus.Document_Sent_Status;
    if (typeof docStatus === 'string' && docStatus.trim()) {
        storedBlocks.push({ label: 'Document Sent', lines: [docStatus.trim()] });
    }

    const resyncBlocks: ErrorBlock[] = [];
    if (resyncErrors?.systemDesign?.trim()) {
        resyncBlocks.push({
            label: 'System Design',
            lines: mapGoodLeapResyncSystemDesignDisplay(resyncErrors.systemDesign.trim(), financePartner),
        });
    }
    if (resyncErrors?.utility?.trim()) {
        resyncBlocks.push({
            label: 'Utility',
            lines: formatGoodLeapSyncResyncDisplayLines(resyncErrors.utility.trim()),
        });
    }
    if (resyncErrors?.documentSent?.trim()) {
        resyncBlocks.push({
            label: 'Document Sent',
            lines: formatGoodLeapSyncResyncDisplayLines(resyncErrors.documentSent.trim()),
        });
    }

    const hasStored = loanSyncHasStoredErrors(loanStatus, financePartner) || storedBlocks.length > 0;

    return (
        <div className="obd-loan-sync-errors">
            <div className="obd-loan-sync-errors__body">
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '8px', marginBottom: '24px' }}>
                    <XCircle size={52} color="#ef4444" weight="light" />
                </div>
                {hasStored ? (
                    storedBlocks.map((block) => <SyncErrorBlock key={`stored-${block.label}`} {...block} />)
                ) : (
                    <div className="obd-loan-sync-errors__empty">No sync errors found.</div>
                )}
                {resyncBlocks.length ? (
                    <>
                        <hr className="obd-loan-sync-errors__divider" />
                        <h4 className="obd-loan-sync-errors__resync-title">Resync Errors</h4>
                        {resyncBlocks.map((block) => (
                            <SyncErrorBlock key={`resync-${block.label}`} {...block} />
                        ))}
                    </>
                ) : null}
            </div>
        </div>
    );
};
